'use client';

import { InfoCircledIcon } from '@radix-ui/react-icons';
import Link from 'next/link';
import { type FocusEvent, type ReactNode, type TouchEvent,useEffect, useState } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/libs/utils';
import { moveFocusToNextElement } from '@/utils/gridUtils';

interface PropTypes {
  contentDescription: string | ReactNode;
  contentDescription2?: string | ReactNode;
  linkDescription?: string;
  id: string;
  btnAria: string;
  linkUrl?: string;
  dataTestIdName?: string;
  className?: string;
  align?: 'center' | 'end' | 'start' | undefined;

  alignOffset?: number;
}

const ids = ['semester-grades-tab'];

const TooltipComp = ({
  contentDescription,
  contentDescription2,
  linkDescription,
  id,
  btnAria,
  linkUrl,
  dataTestIdName,

  alignOffset,
  align = 'center',
  className,
}: PropTypes) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleTouchToggle = (event: TouchEvent) => {
    // Prevent touch triggering unintended behavior
    event.stopPropagation();
    setIsTooltipOpen((prev) => !prev);
  };

  const closeTooltips = () => {
    setIsTooltipOpen(false);
  };

  useEffect(() => {
    const handleOutsideClick = () => closeTooltips();
    window.addEventListener('scroll', closeTooltips);
    window.addEventListener('touchstart', handleOutsideClick);

    return () => {
      window.removeEventListener('scroll', closeTooltips);
      window.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);
  const handleFocusLink = () => {
    setIsTooltipOpen(true);
  };

  const handleBlurLink = (event: FocusEvent<HTMLAnchorElement>) => {
    const activeElement = event.currentTarget as HTMLElement;
    setTimeout(() => {
      setIsTooltipOpen(false);
      moveFocusToNextElement(activeElement, ids);
    }, 0);
  };

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={10}
        open={isTooltipOpen} // Control tooltip open state
        onOpenChange={(open) => setIsTooltipOpen(open)} // Handle tooltip opening/closing
      >
        <TooltipTrigger asChild>
          <button
            className="inline-flex !min-h-[24px] !min-w-[24px] items-center justify-center"
            aria-label={btnAria}
            id={id}
            onClick={(e) => e.preventDefault()} // Prevent default button behavior
            onTouchEnd={handleTouchToggle}
          >
            <InfoCircledIcon className="!size-4 text-spblue" data-testid={dataTestIdName} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          alignOffset={alignOffset && alignOffset}
          aria-describedby={id}
          role="tooltip"
          className={cn(
            'w-[22rem] bg-neutral-100 font-sans text-xs font-normal leading-[1.5em] text-spblack md:w-[25rem]',
            className
          )}
          side="bottom"
          align={align}
        >
          <span>
            {contentDescription}{' '}
            {linkDescription && linkUrl && (
              <Link
                href={linkUrl}
                tabIndex={0}
                aria-describedby={linkDescription}
                target="_blank"
                className="font-bold text-spblue underline hover:underline focus:underline"
                onFocus={handleFocusLink} // Open tooltip when focusing on icon
                onBlur={handleBlurLink} // Close tooltip when leaving link
              >
                {linkDescription} <i className="fal fa-external-link inline text-base" />
                <span className="sr-only">opens in new tabs</span>
              </Link>
            )}
          </span>
          {contentDescription2 && contentDescription2}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TooltipComp;
